import GeoContext from 'contexts/geoContext'
import { shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import useOrder from 'hooks/Order/useOrder'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useLocation, useParams } from 'react-router'
import ChatTripPlannerMobileBlocker from './ChatTripPlannerMobileBlocker'
import { getFirstName, isLEStaff, selectLoggedIn } from 'selectors/accountSelectors'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'
import { TripPlannerEventBusProvider } from './ChatTripPlannerPage/contexts/TripPlannerEventBus'
import { TripPlannerChatProvider } from './ChatTripPlannerPage/contexts/TripPlannerChatProvider'
import { useAnonymousChatClaiming } from './ChatTripPlannerPage/hooks/useAnonymousChatClaiming'
import { useChatHistory } from './ChatTripPlannerPage/hooks/useChatHistory'
import { NEW_CHAT_ROUTE_SEGMENT, useChatNavigation } from './ChatTripPlannerPage/hooks/useChatNavigation'
import { useChatTitleGeneration } from './ChatTripPlannerPage/hooks/useChatTitleGeneration'
import { usePromptManagement } from './ChatTripPlannerPage/hooks/usePromptManagement'
import { PromptManagementProvider, type PromptManagementContextValue } from './ChatTripPlannerPage/contexts/PromptManagementContext'
import ChatPageContent from './ChatTripPlannerPage/ChatPageContent'
import { buildBookingTag, getFlightBookingItemIds, getFlightDetailsStateByItemIds, getOrderTileDataList } from './ChatLandingPage/bookingSelectionUtils'
import { getEffectiveOrderIds, getNextDismissedPrefilledOrderItemIds } from './ChatTripPlannerPage/utils/prefilledOrderSelection'

function ChatTripPlannerPage() {
  const isMobileOrTablet = useIsMobileOrTablet()
  const intl = useIntl()

  const isStaff = useAppSelector(isLEStaff)
  const canManagePrompts = isStaff
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const firstName = useAppSelector(getFirstName)
  const { latitude, longitude } = useContext(GeoContext)
  const { search } = useLocation()
  const userData = useMemo(() => ({
    firstName: firstName || undefined,
    lat: latitude,
    lng: longitude,
  }), [firstName, latitude, longitude])

  const [isDevMode, setIsDevMode] = useState(false)
  const [bookingSelection, setBookingSelection] = useState<{ orderIds: Array<string>; offerIds: Array<string> }>({ orderIds: [], offerIds: [] })
  const [dismissedPrefilledOrderItemIds, setDismissedPrefilledOrderItemIds] = useState<Array<string>>([])
  const { chatId: routeChatIdParam } = useParams<{ chatId?: string }>()
  const orderIdFromQuery = useMemo(() => new URLSearchParams(search).get('orderId') ?? undefined, [search])
  const [orderForChip] = useOrder(orderIdFromQuery)

  const {
    chatId, setChatId: syncChatId, setChatIdForStreaming,
    isNewChatRoute, isLandingRoute, existingRouteChatId, navigateToNewChatRoute,
  } = useChatNavigation({ routeChatId: routeChatIdParam })

  const chatKey = useMemo(
    () => (chatId !== undefined ? String(chatId) : NEW_CHAT_ROUTE_SEGMENT),
    [chatId],
  )

  useEffect(() => {
    setDismissedPrefilledOrderItemIds([])
  }, [orderIdFromQuery])

  const prefilledOrderTiles = useMemo(() => {
    if (!orderForChip) {
      return []
    }

    return getOrderTileDataList(orderForChip)
  }, [orderForChip])

  const prefilledOrderFlightItemIds = useMemo(() => {
    return getFlightBookingItemIds(prefilledOrderTiles)
  }, [prefilledOrderTiles])

  const flightDetailsState = useAppSelector((state) => {
    return getFlightDetailsStateByItemIds(state.orders.flightDetails, prefilledOrderFlightItemIds)
  }, shallowEqual)

  const prefilledOrderTagsByItemId = useMemo(() => {
    const nextPrefilledOrderTagsByItemId = new Map<string, string>()

    for (const tile of prefilledOrderTiles) {
      nextPrefilledOrderTagsByItemId.set(tile.itemId, buildBookingTag({
        intl,
        tile,
        flightDetailsState,
      }))
    }

    return nextPrefilledOrderTagsByItemId
  }, [flightDetailsState, intl, prefilledOrderTiles])

  const dismissedPrefilledOrderItemIdSet = useMemo(() => {
    return new Set(dismissedPrefilledOrderItemIds)
  }, [dismissedPrefilledOrderItemIds])

  const prefilledOrderChipLabels = useMemo(() => {
    return [...new Set(
      prefilledOrderTiles
        .filter(tile => !dismissedPrefilledOrderItemIdSet.has(tile.itemId))
        .map(tile => prefilledOrderTagsByItemId.get(tile.itemId))
        .filter((tag): tag is string => Boolean(tag)),
    )]
  }, [dismissedPrefilledOrderItemIdSet, prefilledOrderTagsByItemId, prefilledOrderTiles])

  const hasCustomizedPrefilledOrderSelection = dismissedPrefilledOrderItemIds.length > 0

  const effectiveOrderIds = useMemo(() => {
    return getEffectiveOrderIds({
      orderIdFromQuery,
      bookingSelectionOrderIds: bookingSelection.orderIds,
      hasCustomizedPrefilledOrderSelection,
    })
  }, [bookingSelection.orderIds, hasCustomizedPrefilledOrderSelection, orderIdFromQuery])

  const {
    refetchChatList, claimChatsMutation,
    updateChatLastActivityTimestamp, startChatTitleGeneration: startTitleGen,
  } = useChatHistory({ isLoggedIn })

  const { startChatTitleGeneration } = useChatTitleGeneration({ startTitleGen })

  const {
    selectedPrompt, promptOptions, selectedPromptKey,
    loadPrompts, handlePromptAdd, handlePromptSelect,
  } = usePromptManagement({ canManagePrompts })

  const promptContextValue = useMemo<PromptManagementContextValue>(() => ({
    promptOptions, selectedPromptKey, loadPrompts, handlePromptAdd, handlePromptSelect,
  }), [promptOptions, selectedPromptKey, loadPrompts, handlePromptAdd, handlePromptSelect])

  const { waitForClaimCompletion } = useAnonymousChatClaiming({
    isLoggedIn,
    claimChatsMutation,
    refetchChatList,
  })

  const handleDevModeToggle = useCallback(() => {
    setIsDevMode((prev) => !prev)
  }, [])

  const handleRemovePrefilledOrderTag = useCallback((tag: string) => {
    setDismissedPrefilledOrderItemIds((prev) => {
      return getNextDismissedPrefilledOrderItemIds({
        tag,
        dismissedPrefilledOrderItemIds: prev,
        prefilledOrderTagsByItemId,
      })
    })
  }, [prefilledOrderTagsByItemId])

  if (isMobileOrTablet) {
    return <ChatTripPlannerMobileBlocker />
  }

  return (
    <PromptManagementProvider value={promptContextValue}>
      <TripPlannerEventBusProvider>
        <TripPlannerChatProvider
          chatId={chatId}
          setChatIdForStreaming={setChatIdForStreaming}
          prompt={selectedPrompt.promptId}
          isDevMode={isDevMode}
          isLoggedIn={isLoggedIn}
          orderIds={effectiveOrderIds}
          offerIds={bookingSelection.offerIds}
          refetchChatList={refetchChatList}
          waitForAnonymousChatClaim={waitForClaimCompletion}
          startChatTitleGeneration={startChatTitleGeneration}
          updateChatLastActivityTimestamp={updateChatLastActivityTimestamp}
          userData={userData}
          chatKey={chatKey}
        >
          <ChatPageContent
            chatId={chatId}
            syncChatId={syncChatId}
            isNewChatRoute={isNewChatRoute}
            isLandingRoute={isLandingRoute}
            existingRouteChatId={existingRouteChatId}
            navigateToNewChatRoute={navigateToNewChatRoute}
            isDevMode={isDevMode}
            onDevModeToggle={handleDevModeToggle}
            setBookingSelection={setBookingSelection}
            prefilledOrderChipLabels={prefilledOrderChipLabels}
            onRemovePrefilledOrderTag={handleRemovePrefilledOrderTag}
          />
        </TripPlannerChatProvider>
      </TripPlannerEventBusProvider>
    </PromptManagementProvider>
  )
}

export default ChatTripPlannerPage
